// Calendar utilities: pure functions for grid layout and post grouping.
//
// Timezone contract: grid CELLS are plain calendar dates (a Sep-5 cell is
// Sep 5), keyed "YYYY-MM-DD" from local getters on the midnight-local Date the
// grid builds. A POST buckets onto the calendar date it falls on IN THE CHOSEN
// ZONE via dayKeyInTz(at, tz) — same "YYYY-MM-DD" shape — so a late-evening post
// stays on the day the user meant in their timezone. `today` highlighting uses
// the chosen zone too. When tz is the browser's own zone (the default), this is
// identical to the previous local-only behavior.

import type { DispatchRecord, ScheduledPost } from "../api";
import { browserTimezone, dayKeyInTz } from "./tz";

/** One cell in the calendar grid. */
export interface CalendarDay {
  /** Midnight LOCAL time on this calendar date. */
  date: Date;
  /** False for leading/trailing days that belong to the prev/next month. */
  isCurrentMonth: boolean;
  /** True when this cell is today (compared by local year/month/day). */
  isToday: boolean;
}

/** A grid of week rows (Sunday–Saturday). Typically 4–6 rows. */
export type CalendarGrid = CalendarDay[][];

/**
 * A stable "YYYY-MM-DD" key for a grid cell's calendar date, from LOCAL getters
 * on the midnight-local Date the grid builds. Matches the shape of
 * {@link dayKeyInTz} so a post bucketed by its chosen-zone date lands in the
 * cell of the same calendar date.
 */
export function localDayKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Build the month grid for a given year and 0-based month.
 *
 * The grid starts on the Sunday of the week that contains the 1st of the month
 * and ends on the Saturday of the week that contains the last day. Leading and
 * trailing cells outside the target month have `isCurrentMonth: false` but
 * carry their real Date so posts scheduled on those days still appear.
 *
 * `today` is injected (not read from Date.now()) so tests stay deterministic.
 * `tz` decides which cell is "today" — the chosen zone's current date — so the
 * highlight follows the user's timezone, not the browser's, at a day boundary.
 */
export function buildMonthGrid(
  year: number,
  month: number,
  today: Date,
  tz: string = browserTimezone(),
): CalendarGrid {
  const firstOfMonth = new Date(year, month, 1);
  // day 0 of next month == last day of this month in JS.
  const lastOfMonth = new Date(year, month + 1, 0);

  // Number of blank cells before the 1st (0 when the 1st falls on Sunday).
  const leadingBlanks = firstOfMonth.getDay(); // 0=Sun … 6=Sat

  // Number of blank cells after the last day (0 when it falls on Saturday).
  const trailingBlanks = 6 - lastOfMonth.getDay();

  const totalDays = leadingBlanks + lastOfMonth.getDate() + trailingBlanks;

  // The Sunday at or before the 1st — local date arithmetic via the constructor
  // (passing day 0, -1, … rolls back to the prior month correctly in JS).
  const gridStartDay = firstOfMonth.getDate() - leadingBlanks;
  const todayKey = dayKeyInTz(today.getTime(), tz);

  const weeks: CalendarGrid = [];
  let week: CalendarDay[] = [];

  for (let i = 0; i < totalDays; i++) {
    // Build each date by offsetting from the grid start using the local
    // constructor — avoids any DST pitfall that would arise from adding
    // 86_400_000 ms to a Date (clocks skip/repeat an hour near DST changes).
    const date = new Date(
      firstOfMonth.getFullYear(),
      firstOfMonth.getMonth(),
      gridStartDay + i,
    );

    week.push({
      date,
      isCurrentMonth: date.getMonth() === month,
      isToday: localDayKey(date) === todayKey,
    });

    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }

  // Safety net: a partial last row (should never occur given the trailing-blank
  // calculation, but guard it so the grid is always valid).
  if (week.length > 0) weeks.push(week);

  return weeks;
}

/**
 * Build 7 CalendarDays (Sun–Sat) for the week that contains `anchor`.
 * Used for Week view. The week spans whatever months are needed. `tz` decides
 * which day is "today" (the chosen zone's current date).
 */
export function buildWeekDays(
  anchor: Date,
  today: Date,
  tz: string = browserTimezone(),
): CalendarDay[] {
  // anchor.getDay() gives 0 (Sun) … 6 (Sat); subtract to reach Sunday.
  const sundayOffset = anchor.getDay();
  const anchorMonth = anchor.getMonth(); // used as a loose "current month" label
  const todayKey = dayKeyInTz(today.getTime(), tz);
  const days: CalendarDay[] = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date(
      anchor.getFullYear(),
      anchor.getMonth(),
      anchor.getDate() - sundayOffset + i,
    );
    days.push({
      date,
      // In week view, "isCurrentMonth" marks which days belong to the
      // anchor's month — used for subtle visual context only.
      isCurrentMonth: date.getMonth() === anchorMonth,
      isToday: localDayKey(date) === todayKey,
    });
  }

  return days;
}

// ---------------------------------------------------------------------------
// Unified calendar items: SCHEDULED (upcoming) + PUBLISHED (already fired)
// ---------------------------------------------------------------------------
//
// The calendar draws from two sources that never overlap in normal operation:
//   - `scheduled`: future rows waiting to fire. Deleted from the schedule the
//     instant they publish.
//   - `history`:   DispatchRecords for posts that already went out (scheduled
//     posts after they fire, AND manually-dispatched posts that were never
//     scheduled at all).
// A given post is therefore in EITHER list, never both — so a day that already
// published no longer shows as empty once we fold history in.
//
// Both kinds are placed on their LOCAL calendar day (localDayKey), never UTC,
// so a late-evening post stays on the day the user actually posted it.

/** A calendar item that has not yet fired — the existing scheduled-post case. */
export interface ScheduledItem {
  kind: "scheduled";
  /** The schedule row id (stable key + cancel target). */
  id: string;
  /** Epoch ms the post is scheduled to fire — the day it lands on. */
  at: number;
  /** Draft title (may be empty). */
  title: string;
  post: ScheduledPost;
}

/** A calendar item that already published — folded in from dispatch history. */
export interface PublishedItem {
  kind: "published";
  /** The dispatch-record id (stable key). */
  id: string;
  /** Epoch ms the post actually went out — the day it lands on. */
  at: number;
  title: string;
  record: DispatchRecord;
}

/** A day-cell entry is one or the other; callers branch on `kind`. */
export type CalendarItem = ScheduledItem | PublishedItem;

/**
 * Dedupe one day's items. A post is normally in EITHER `scheduled` OR `history`,
 * never both (firing deletes the scheduled row), so this is a defensive guard:
 *
 *   - Drop exact duplicate ids (same list carrying a post twice).
 *   - Drop a SCHEDULED item whose title matches a PUBLISHED item on the same
 *     day — that scheduled row is a stale echo of a post that already fired,
 *     and the published record is the real outcome, so it wins.
 *
 * Published-vs-published are never merged by title: two distinct manual posts
 * (e.g. an X "test" and a failed X attempt on the same day) must both survive.
 */
function dedupeDayItems(bucket: CalendarItem[]): CalendarItem[] {
  const publishedTitles = new Set<string>();
  for (const item of bucket) {
    if (item.kind === "published") publishedTitles.add(item.title);
  }

  const seenIds = new Set<string>();
  const out: CalendarItem[] = [];
  for (const item of bucket) {
    if (seenIds.has(item.id)) continue;
    if (item.kind === "scheduled" && publishedTitles.has(item.title)) continue;
    seenIds.add(item.id);
    out.push(item);
  }
  return out;
}

/**
 * Group scheduled posts AND published dispatch records into per-local-day
 * buckets, each sorted by ascending time. O(n) over the combined input.
 *
 * Published records are inserted first so they win any dedupe tie — the record
 * of what actually happened outranks a lingering schedule row for the same post.
 */
export function groupCalendarItems(
  scheduled: ScheduledPost[],
  history: DispatchRecord[],
  tz: string = browserTimezone(),
): Map<string, CalendarItem[]> {
  const groups = new Map<string, CalendarItem[]>();

  const add = (key: string, item: CalendarItem) => {
    const bucket = groups.get(key);
    if (bucket) bucket.push(item);
    else groups.set(key, [item]);
  };

  // Published first (see dedupe rationale above). Bucketed by the chosen zone's
  // calendar date, matching the grid cell keys (localDayKey).
  for (const record of history) {
    const key = dayKeyInTz(record.at, tz);
    add(key, {
      kind: "published",
      id: record.id,
      at: record.at,
      title: record.title,
      record,
    });
  }

  for (const post of scheduled) {
    const key = dayKeyInTz(post.at, tz);
    add(key, {
      kind: "scheduled",
      id: post.id,
      at: post.at,
      title: post.draft.title,
      post,
    });
  }

  for (const [key, bucket] of groups) {
    const deduped = dedupeDayItems(bucket);
    deduped.sort((a, b) => a.at - b.at);
    groups.set(key, deduped);
  }

  return groups;
}
